/**
 * 检查字符串是否为驼峰命名
 * @param str 要检查的字符串
 * @returns 是否为驼峰命名
 */
function isCamelCase(str: string): boolean {
  // 驼峰命名的特征：包含大写字母，且不在开头
  return /[A-Z]/.test(str) && str[0] === str[0].toLowerCase();
}

/**
 * 检查字符串是否为下划线命名
 * @param str 要检查的字符串
 * @returns 是否为下划线命名
 */
function isSnakeCase(str: string): boolean {
  // 下划线命名的特征：包含下划线，且全小写
  return str.includes('_') && str === str.toLowerCase();
}

/**
 * 将驼峰命名转换为下划线命名
 * @param str 驼峰命名字符串
 * @returns 下划线命名字符串
 */
function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * 将下划线命名转换为驼峰命名
 * @param str 下划线命名字符串
 * @returns 驼峰命名字符串
 */
function snakeToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
}

/**
 * 智能转换字段名：只转换驼峰命名的字段，保持下划线字段不变
 * @param key 字段名
 * @returns 转换后的字段名
 */
function smartConvertKey(key: string): string {
  // 如果已经是下划线格式，直接返回
  if (key.includes('_')) {
    return key;
  }
  
  // 如果是驼峰命名，转换为下划线
  if (isCamelCase(key)) {
    return camelToSnakeCase(key);
  }
  
  // 其他情况（全小写、全大写等）保持原样
  return key;
}

/**
 * 智能转换字段名：只转换下划线命名的字段，保持驼峰字段不变
 * @param key 字段名
 * @returns 转换后的字段名
 */
function smartConvertKeyToCamel(key: string): string {
  // 如果已经是驼峰格式，直接返回
  if (isCamelCase(key)) {
    return key;
  }
  
  // 如果是下划线命名，转换为驼峰
  if (isSnakeCase(key)) {
    return snakeToCamelCase(key);
  }
  
  // 其他情况（全小写、全大写等）保持原样
  return key;
}

/**
 * 将JSON对象的所有字段名智能转换为下划线命名
 * @param data 要转换的数据
 * @returns 转换后的数据
 */
function convertCamelToSnake<T>(data: T): T {
  // 处理null和undefined
  if (data === null || data === undefined) {
    return data;
  }
  
  // 处理数组
  if (Array.isArray(data)) {
    return data.map(item => convertCamelToSnake(item)) as T;
  }
  
  // 处理对象
  if (typeof data === 'object') {
    const result: Record<string, any> = {};
    
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        // 智能转换字段名
        const convertedKey = smartConvertKey(key);
        // 递归处理值
        result[convertedKey] = convertCamelToSnake((data as any)[key]);
      }
    }
    
    return result as T;
  }
  
  // 处理基本类型（字符串、数字、布尔值等）
  return data;
}

/**
 * 将JSON对象的所有字段名智能转换为驼峰命名
 * @param data 要转换的数据
 * @returns 转换后的数据
 */
function convertSnakeToCamel<T>(data: T): T {
  // 处理null和undefined
  if (data === null || data === undefined) {
    return data;
  }
  
  // 处理数组
  if (Array.isArray(data)) {
    return data.map(item => convertSnakeToCamel(item)) as T;
  }
  
  // 处理对象
  if (typeof data === 'object') {
    const result: Record<string, any> = {};
    
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        // 智能转换字段名
        const convertedKey = smartConvertKeyToCamel(key);
        // 递归处理值
        result[convertedKey] = convertSnakeToCamel((data as any)[key]);
      }
    }
    
    return result as T;
  }
  
  // 处理基本类型（字符串、数字、布尔值等）
  return data;
}

// 导出类型定义
export interface FieldConverter {
  convertCamelToSnake: <T>(data: T) => T;
  convertSnakeToCamel: <T>(data: T) => T;
  // smartConvertKey: (key: string) => string;
  // smartConvertKeyToCamel: (key: string) => string;
  isCamelCase: (str: string) => boolean;
  isSnakeCase: (str: string) => boolean;
  camelToSnakeCase: (str: string) => string;
  snakeToCamelCase: (str: string) => string;
}

// 默认导出
const fieldConverter: FieldConverter = {
  convertCamelToSnake,
  convertSnakeToCamel,
  isCamelCase,
  isSnakeCase,
  camelToSnakeCase,
  snakeToCamelCase
};

export default fieldConverter;

// 也可以单独导出函数
export {
  convertCamelToSnake,
  convertSnakeToCamel,
  isCamelCase,
  isSnakeCase,
  camelToSnakeCase,
  snakeToCamelCase
};
